/**
 * 前に読んだログインの状態の控え（docs/screens/06 §3）。
 *
 * ヘッダーの出入口と管理センターの入口は、<c>/api/session</c> を読むまで分からない。
 * 読んでから出すと、ページを開くたびに少し遅れて出る。<b>前に読んだ状態をこのタブの sessionStorage に控え、
 * 次に開いたページでは最初の描画の前に当てる</b>（<c>ClientApp/head.ts</c>。<c>&lt;head&gt;</c> で同期的に読む）。
 *
 * <b>控えは見た目のためだけ。</b>最後に決めるのは毎回の <c>/api/session</c> の結果で
 * （<c>header-session.ts</c>）、管理センターを開けるかどうかはサーバが毎回判定する。
 * 控えを書き換えられても、出るのは入口の見た目だけで、開ける画面は増えない。
 *
 * <b>入れるのは見た目に要るものだけ</b>（ログイン中か・管理センターの入口を出すか・アドレス）。トークンは入れない。
 * 読み書きできない（プライベートな窓など）・壊れているときは控えが無いのと同じ——<c>/api/session</c> を読んでから出るだけ。
 * 会員の画面（<c>MemberApp/app/auth-hint.ts</c>）も同じ名前・同じ形で書く。
 */

/** 控えの名前。 */
export const authHintKey = "portal.authHint";

/** 控えを当てた印（<c>&lt;html data-auth-hint="signed-in admin"&gt;</c>）。CSS が見る（<c>styles/navigation.css</c>）。 */
export const authHintAttribute = "data-auth-hint";

/** 控え。ログインしていないときは控えを持たない（書き出したままの見た目がログイン前）。 */
export interface AuthHint {
    readonly signedIn: true;
    readonly admin: boolean;
    readonly email: string;
}

/** アドレスの長さの上限。これより長いものは控えとして扱わない（壊れている）。 */
const maxEmailLength = 320;

const storage = (): Storage | undefined => {
    try {
        return globalThis.sessionStorage;
    } catch {
        return undefined;
    }
};

const isAuthHint = (value: unknown): value is AuthHint => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const hint = value as Record<string, unknown>;
    return hint["signedIn"] === true
        && typeof hint["admin"] === "boolean"
        && typeof hint["email"] === "string"
        && hint["email"].length <= maxEmailLength;
};

/** 控えを読む。無い・読めない・形が違うなら undefined。 */
export const readAuthHint = (): AuthHint | undefined => {
    try {
        const text = storage()?.getItem(authHintKey);
        if (text === null || text === undefined) {
            return undefined;
        }
        const value: unknown = JSON.parse(text);
        return isAuthHint(value) ? value : undefined;
    } catch {
        return undefined;
    }
};

/** 読んだ状態を控える。ログインしていなければ控えを消す。 */
export const writeAuthHint = (session: {
    readonly signedIn: boolean;
    readonly email: string | null;
    readonly canOpenAdmin?: boolean | undefined;
}): void => {
    if (!session.signedIn) {
        clearAuthHint();
        return;
    }
    const hint: AuthHint = { signedIn: true, admin: session.canOpenAdmin === true, email: session.email ?? "" };
    try {
        storage()?.setItem(authHintKey, JSON.stringify(hint));
    } catch {
        // 書けなければ、次のページも /api/session を読んでから出るだけ
    }
};

/** 控えを消す（ログアウト・ログインが切れたとき）。 */
export const clearAuthHint = (): void => {
    try {
        storage()?.removeItem(authHintKey);
    } catch {
        // 消せなくても、次のページで /api/session の結果に直る
    }
};

/**
 * 控えを最初の描画の前に当てる（<c>head.ts</c> から）。<b>&lt;head&gt; で読むので、まだ本文が無い。</b>
 * 入口の出し分けは <c>&lt;html&gt;</c> の属性と CSS で行い、アドレスは要素が読み込まれたところで
 * <b>文字として</b>入れる（MutationObserver。描く前に呼ばれる）。
 * 印 <c>&lt;html data-static-site&gt;</c> の無い画面では何もしない。
 */
export const applyAuthHint = (root: Document = document): void => {
    if (!("staticSite" in root.documentElement.dataset)) {
        return;
    }
    const hint = readAuthHint();
    if (hint === undefined) {
        return;
    }
    root.documentElement.setAttribute(authHintAttribute, hint.admin ? "signed-in admin" : "signed-in");

    const fillEmail = (): boolean => {
        const email = root.querySelector<HTMLElement>("[data-auth-email]");
        if (email === null) {
            return false;
        }
        if (email.textContent === "") {
            email.textContent = hint.email;
        }
        return true;
    };
    // 窓の持ち物から取る（テストの jsdom でも同じ道を通る）
    const view = root.defaultView;
    if (fillEmail() || view === null) {
        return;
    }
    const observer = new view.MutationObserver(() => {
        if (fillEmail()) {
            observer.disconnect();
        }
    });
    observer.observe(root.documentElement, { childList: true, subtree: true });
    root.addEventListener("DOMContentLoaded", () => {
        observer.disconnect();
    }, { once: true });
};

/** 控えを当てた印を外す。ここから先は <c>/api/session</c> の結果（hidden）だけで出し分ける。 */
export const dropAuthHint = (root: Document): void => {
    root.documentElement.removeAttribute(authHintAttribute);
};
